import httpClient from "@plugins/http";
import { notNull } from "@utils/var.check.ts";
import {
  MediaObjectCopyRightModel,
  MediaObjectHtml5Payload,
  MediaObjectImagePayload,
  MediaObjectResponse,
  MediaObjectWorkflowModel,
  MediaObjectEditModel,
  TranscriptionJobModel,
  MediaObjectManageModel,
  MediaObjectPreCreatePayload,
} from "@plugins/media/types.ts";
import { ApiResponse } from "@apis/api.ts";
import {
  DownloadMediaObjectParams,
  GetMetadataParams,
  MediaObjectMetadata,
  MediaObjectModel,
} from "@plugins/richtext/types.ts";

const createMediaObject = (requestData) => {
  return httpClient.post(`/apis/mediaobject/create`, requestData);
};

const saveMediaObject = (requestData): Promise<ApiResponse> => {
  const { finalize, ...data } = requestData;
  return httpClient.post(`/apis/mediaobject/save?finalize=${finalize || false}`, data);
};

const getMetaData = (params: GetMetadataParams): Promise<ApiResponse<MediaObjectMetadata>> => {
  const queryParams = new URLSearchParams();
  if (params.contextUuid && params.contextType) {
    queryParams.append("contextType", params.contextType);
    queryParams.append("contextUuid", params.contextUuid);
  }
  if (params.dataDisplayStart) {
    queryParams.append("dataDisplayStart", String(params.dataDisplayStart));
  }
  return httpClient.get(`/apis/mediaobject/get-meta/${params.uuid}`, { params: queryParams });
};

const generateAudioFromText = (params): Promise<ApiResponse<MediaObjectModel>> => {
  return httpClient.post(`/apis/mediaobject/generate-tts`, params);
};

const updateAudioFromText = (params): Promise<ApiResponse<MediaObjectModel>> => {
  return httpClient.post(`/apis/mediaobject/update-tts`, params);
};

const checkMoStatus = (uuid) => {
  return httpClient.get(`/apis/mediaobject/check-lesson-mo-status/${uuid}`);
};

const duplicateMediaObject = (params) => {
  return httpClient.post(`/apis/mediaobject/duplicate`, params, {});
};

export const downloadMediaObject = (
  params: DownloadMediaObjectParams,
): Promise<ApiResponse<string>> => {
  const queryParams = new URLSearchParams();
  if (params.contextUuid && params.contextType) {
    queryParams.append("contextType", String(params.contextType));
    queryParams.append("contextUuid", params.contextUuid);
  }
  return httpClient.get(`/apis/mediaobject/download/${params.uuid}`, { params: queryParams });
};

const viewPdf = (params) => {
  const queryParams = new URLSearchParams();
  if (params.contextUuid && params.contextType) {
    queryParams.append("contextType", params.contextType);
    queryParams.append("contextUuid", params.contextUuid);
  }
  return httpClient.get(`/apis/pdf/view/${params.uuid}`, { params: queryParams });
};

const editMediaObject = (uuid: string): Promise<ApiResponse<MediaObjectEditModel>> => {
  return httpClient.post(`/apis/mediaobject/edit/${uuid}`, {
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
    },
  });
};

const fetchManageMediaObjectList = (requestData) => {
  const data = new URLSearchParams();
  Object.keys(requestData).forEach((key) => {
    if (notNull(requestData[key])) {
      data.append(key, requestData[key]);
    }
  });
  return httpClient.get(`/apis/mediaobject/manage-resource/list`, {
    params: data,
  });
};

const fetchMediaObjectCopyrightData = (uuid) => {
  return httpClient.get(`/apis/mediaobject/copyright/${uuid}`);
};

const fetchMediaObjectLinkedResource = (requestData) => {
  return httpClient.get(`/apis/mediaobject/linked-resource/list`, {
    params: requestData,
  });
};

const updateMediaObjectStatusToUnpublish = (
  uuid: string,
): Promise<ApiResponse<MediaObjectWorkflowModel>> => {
  return httpClient.post(`/apis/mediaobject/unpublish/${uuid}`);
};

const updateMediaObjectStatusToApproved = (
  uuid: string,
): Promise<ApiResponse<MediaObjectWorkflowModel>> => {
  return httpClient.post(`/apis/mediaobject/approve/${uuid}`);
};

const updateMediaObjectStatusToSubmit = (
  uuid: string,
): Promise<ApiResponse<MediaObjectWorkflowModel>> => {
  return httpClient.post(`/apis/mediaobject/submit/${uuid}`);
};

const updateMediaObjectStatusToRejected = (
  uuid: string,
): Promise<ApiResponse<MediaObjectWorkflowModel>> => {
  return httpClient.post(`/apis/mediaobject/reject/${uuid}`);
};

const getMoImageBlob = (path) => {
  return httpClient.get(`${path}`, {
    withCredentials: true,
    responseType: "blob",
  });
};

const transcribeMediaObject = (
  uuid: string,
  requestData: { lessonUuid: string; activityId: number },
): Promise<ApiResponse<{ fileVersion: string }>> => {
  return httpClient.post(`/apis/mediaobject/transcript/${uuid}`, "", {
    params: requestData,
  });
};

const getTranscriptStatus = (uuid: string): Promise<ApiResponse<string>> => {
  return httpClient.get(`/apis/mediaobject/transcript/status/${uuid}`);
};

const saveMediaObjectTranscript = (requestData): Promise<ApiResponse<TranscriptionJobModel>> => {
  return httpClient.post(`/apis/mediaobject/transcript/save`, requestData);
};

const getTranscriptJob = (
  mediaObjectUuid: string,
  requestParams: { params: { fileVersion?: string } },
): Promise<ApiResponse<TranscriptionJobModel>> => {
  return httpClient.get(`/apis/mediaobject/transcript/fetch/${mediaObjectUuid}`, requestParams);
};

const deleteMediaObject = (uuid: string): Promise<ApiResponse<void>> => {
  return httpClient.post(`/apis/mediaobject/softdelete/${uuid}`);
};

const getMediaObjectIPDetails = (uuid: string): Promise<ApiResponse<MediaObjectCopyRightModel>> => {
  return httpClient.get(`/apis/mediaobject/ip/manage/details/${uuid}`);
};

const saveMediaObjectIPDetails = (
  requestData: MediaObjectCopyRightModel,
): Promise<ApiResponse<MediaObjectCopyRightModel>> => {
  return httpClient.post(`/apis/mediaobject/ip/manage/edit`, requestData);
};

const getMediaObjectDetails = (uuid: string): Promise<ApiResponse<MediaObjectManageModel>> => {
  return httpClient.get(`/apis/mediaobject/manage/details/${uuid}`);
};

const editMediaObjectDetails = (requestData): Promise<ApiResponse<MediaObjectManageModel>> => {
  const { finalize, ...data } = requestData;
  return httpClient.post(`/apis/mediaobject/manage/edit?finalize=${finalize || false}`, data);
};

const fetchMediaObjectManageInfos = (moUuids) => {
  return httpClient.get(`/apis/mediaobject/manage/info/list`, { params: { uuids: moUuids } });
};

const createHtml5MediaObject = async (
  mediaObjectHtml5Request: MediaObjectHtml5Payload,
): Promise<ApiResponse<MediaObjectResponse>> => {
  return await httpClient.post(`/apis/mediaobject/html5/create`, mediaObjectHtml5Request);
};

const createImageMediaObject = async (
  mediaObjectImageRequest: MediaObjectImagePayload,
): Promise<ApiResponse<MediaObjectResponse>> => {
  return await httpClient.post(`/apis/mediaobject/image/create`, mediaObjectImageRequest);
};

const preCreateMo = (requestData: MediaObjectPreCreatePayload): Promise<ApiResponse<string>> => {
  return httpClient.post(`/apis/mediaobject/pre-create`, requestData);
};

export default {
  createMediaObject,
  saveMediaObject,
  getMetaData,
  generateAudioFromText,
  updateAudioFromText,
  checkMoStatus,
  duplicateMediaObject,
  downloadMediaObject,
  viewPdf,
  editMediaObject,
  fetchManageMediaObjectList,
  fetchMediaObjectCopyrightData,
  fetchMediaObjectLinkedResource,
  updateMediaObjectStatusToUnpublish,
  updateMediaObjectStatusToApproved,
  updateMediaObjectStatusToSubmit,
  updateMediaObjectStatusToRejected,
  getMoImageBlob,
  transcribeMediaObject,
  getTranscriptStatus,
  saveMediaObjectTranscript,
  getTranscriptJob,
  deleteMediaObject,
  getMediaObjectIPDetails,
  saveMediaObjectIPDetails,
  getMediaObjectDetails,
  editMediaObjectDetails,
  fetchMediaObjectManageInfos,
  createHtml5MediaObject,
  createImageMediaObject,
  preCreateMo,
};
